/**
 * Конфигурация маршрутизации АСУБП.
 * Все страницы загружаются лениво (React.lazy + Suspense) для ускорения первой загрузки.
 */
import { lazy, Suspense, type ComponentType, type LazyExoticComponent } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import AppLayout from '@/layouts/AppLayout';
import AuthLayout from '@/layouts/AuthLayout';
import ProtectedRoute from '@/components/ProtectedRoute';
import RoleRedirect from '@/components/RoleRedirect';

// ── Обёртка для lazy-import: при ошибке загрузки чанка (после деплоя) —
//    перезагрузить страницу один раз ────────────────────────────
// Bound `ComponentType<unknown>` несовместим с React.FC<{}> (контравариантность
// пропсов). Suspense сам не передаёт пропсы страницам — ограничение по типу
// пропсов нам тут не нужно, поэтому `ComponentType<any>`.
function lazyRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(() =>
    factory().catch(() => {
      const key = 'chunk_reload';
      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, '1');
        window.location.reload();
      }
      return factory();
    }),
  );
}

// ── Страницы (ленивая загрузка) ─────────────────────────────
const LoginPage              = lazyRetry(() => import('@/pages/LoginPage'));
const DashboardPage          = lazyRetry(() => import('@/pages/DashboardPage'));
const NotFoundPage           = lazyRetry(() => import('@/pages/NotFoundPage'));

// Полёты
const FlightsPage            = lazyRetry(() => import('@/pages/FlightsPage'));
const FlightDetailPage       = lazyRetry(() => import('@/pages/FlightDetailPage'));

// Безопасность
const SafetyEventsPage       = lazyRetry(() => import('@/pages/SafetyEventsPage'));
const SafetyEventDetailPage  = lazyRetry(() => import('@/pages/SafetyEventDetailPage'));
const RiskMapPage            = lazyRetry(() => import('@/pages/RiskMapPage'));
const EventCatalogPage       = lazyRetry(() => import('@/pages/EventCatalogPage'));

// Аналитика и отчёты
const AnalyticsPage          = lazyRetry(() => import('@/pages/AnalyticsPage'));
const ReportsPage            = lazyRetry(() => import('@/pages/ReportsPage'));

// Справочники
const FleetPage              = lazyRetry(() => import('@/pages/FleetPage'));
const AircraftDetailPage     = lazyRetry(() => import('@/pages/AircraftDetailPage'));
const AircraftTypeDetailPage = lazyRetry(() => import('@/pages/AircraftTypeDetailPage'));
const AirportsPage           = lazyRetry(() => import('@/pages/AirportsPage'));
const AirportDetailPage      = lazyRetry(() => import('@/pages/AirportDetailPage'));
const SquadronsPage          = lazyRetry(() => import('@/pages/SquadronsPage'));
const PilotsPage             = lazyRetry(() => import('@/pages/PilotsPage'));

// Администрирование
const UsersPage              = lazyRetry(() => import('@/pages/UsersPage'));
const UserDetailPage         = lazyRetry(() => import('@/pages/UserDetailPage'));
const RolesPage              = lazyRetry(() => import('@/pages/RolesPage'));
const ServicesPage           = lazyRetry(() => import('@/pages/ServicesPage'));
const SettingsPage           = lazyRetry(() => import('@/pages/SettingsPage'));
const AuditLogPage           = lazyRetry(() => import('@/pages/AuditLogPage'));
const AuthJournalPage        = lazyRetry(() => import('@/pages/AuthJournalPage'));

// ГРПИ
const AccountingJournalPage  = lazyRetry(() => import('@/pages/AccountingJournalPage'));

// Анализ параметров
const RegInfoAnalysisPage    = lazyRetry(() => import('@/pages/RegInfoAnalysisPage'));

// Мониторинг
const SystemStatusPage       = lazyRetry(() => import('@/pages/SystemStatusPage'));

// Карточка пилота
const PilotDetailPage        = lazyRetry(() => import('@/pages/PilotDetailPage'));

// Синхронизация
const SyncManagementPage     = lazyRetry(() => import('@/pages/SyncManagementPage'));

// Рабочие места
const PilotWorkspacePage     = lazyRetry(() => import('@/pages/PilotWorkspacePage'));
const ServiceWorkspacePage   = lazyRetry(() => import('@/pages/ServiceWorkspacePage'));

// Поиск
const SearchPage             = lazyRetry(() => import('@/pages/SearchPage'));

// Пользователь
const ProfilePage            = lazyRetry(() => import('@/pages/ProfilePage'));
const NotificationsPage      = lazyRetry(() => import('@/pages/NotificationsPage'));

// Помощь
const HelpPage               = lazyRetry(() => import('@/pages/HelpPage'));

// ── Заглушка загрузки ────────────────────────────────────────
const PageLoader = () => (
  <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
    <CircularProgress />
  </Box>
);

/** Оборачивает ленивый компонент в Suspense. */
function L(Component: React.ComponentType) {
  return (
    <Suspense fallback={<PageLoader />}>
      <Component />
    </Suspense>
  );
}

/**
 * Маршрутизатор приложения.
 */
const router = createBrowserRouter([
  // Публичные маршруты (авторизация)
  {
    element: <AuthLayout />,
    children: [
      {
        path: '/login',
        element: L(LoginPage),
      },
    ],
  },

  // Защищённые маршруты (требуют авторизации)
  {
    element: <ProtectedRoute />,
    children: [
      {
        element: <AppLayout />,
        children: [
          // Перенаправление с корня на страницу по роли
          {
            path: '/',
            element: <RoleRedirect />,
          },

          // Главная
          {
            path: '/dashboard',
            element: L(DashboardPage),
          },

          // Полёты
          {
            path: '/flights',
            element: L(FlightsPage),
          },
          {
            path: '/flights/:id',
            element: L(FlightDetailPage),
          },

          // Безопасность
          {
            path: '/safety/events',
            element: L(SafetyEventsPage),
          },
          {
            path: '/safety/events/:id',
            element: L(SafetyEventDetailPage),
          },
          {
            path: '/safety/risk-map',
            element: L(RiskMapPage),
          },
          {
            path: '/safety/catalog',
            element: L(EventCatalogPage),
          },

          // Аналитика
          {
            path: '/analytics',
            element: L(AnalyticsPage),
          },

          // Отчёты
          {
            path: '/reports',
            element: L(ReportsPage),
          },

          // Справочники
          {
            path: '/fleet',
            element: L(FleetPage),
          },
          {
            path: '/aircraft/:id',
            element: L(AircraftDetailPage),
          },
          {
            path: '/aircraft-types/:id',
            element: L(AircraftTypeDetailPage),
          },
          {
            path: '/airports',
            element: L(AirportsPage),
          },
          {
            path: '/airports/:id',
            element: L(AirportDetailPage),
          },
          {
            path: '/organization',
            element: L(SquadronsPage),
          },
          {
            path: '/pilots',
            element: L(PilotsPage),
          },
          {
            path: '/pilots/:id',
            element: L(PilotDetailPage),
          },

          // Администрирование
          {
            path: '/admin/users',
            element: L(UsersPage),
          },
          {
            path: '/admin/users/:userId',
            element: L(UserDetailPage),
          },
          {
            path: '/admin/roles',
            element: L(RolesPage),
          },
          {
            path: '/admin/services',
            element: L(ServicesPage),
          },
          {
            path: '/admin/settings',
            element: L(SettingsPage),
          },
          {
            path: '/audit-log',
            element: L(AuditLogPage),
          },
          {
            path: '/auth-journal',
            element: L(AuthJournalPage),
          },

          // Журнал смены ГРПИ
          {
            path: '/accounting-journal',
            element: L(AccountingJournalPage),
          },

          // Анализ параметров RegInfo
          {
            path: '/reginfo-analysis',
            element: L(RegInfoAnalysisPage),
          },

          // Мониторинг системы
          {
            path: '/system-status',
            element: L(SystemStatusPage),
          },

          // Синхронизация MDB
          {
            path: '/sync',
            element: L(SyncManagementPage),
          },

          // Рабочее место пилота
          {
            path: '/pilot-workspace',
            element: L(PilotWorkspacePage),
          },
          {
            path: '/pilot-workspace/:id',
            element: L(PilotWorkspacePage),
          },

          // Рабочее место службы
          {
            path: '/service/:serviceCode',
            element: L(ServiceWorkspacePage),
          },

          // Поиск
          {
            path: '/search',
            element: L(SearchPage),
          },

          // Профиль
          {
            path: '/profile',
            element: L(ProfilePage),
          },

          // Уведомления
          {
            path: '/notifications',
            element: L(NotificationsPage),
          },

          // Помощь
          {
            path: '/help',
            element: L(HelpPage),
          },

          // Обратная совместимость (старые URL пилота)
          {
            path: '/pilot-workspace-old',
            element: <Navigate to="/pilot-workspace" replace />,
          },

          // 404 — внутри макета
          {
            path: '*',
            element: L(NotFoundPage),
          },
        ],
      },
    ],
  },
]);

export default router;
